/** @jsx h */
import { component, h } from 'literaljs';
import { playBloop, playPop, playChime } from '../../utils/sounds';
import { apiData } from '../Learn/data';
import { showToast } from '../../utils/toast';
import MethodCard from '../Learn/MethodCard';
import TodoLesson from '../Learn/TodoLesson';
import RouterDocs from '../Learn/RouterDocs';

function filterBySubstring(query, items) {
	if (!query) return items;
	const q = query.toLowerCase();
	return items.filter(item => {
		const text = `${item.name} ${item.description} ${item.tags?.join(' ') || ''}`.toLowerCase();
		return text.includes(q);
	});
}

export default component({
	name: 'LearnPage',
	state: {
		activeTab: 'api',
		searchQuery: '',
		selectedCategory: 'all',
		selectedTag: 'all',
		showSearch: false
	},
	mounted() {
		const hash = window.location.hash.slice(1);
		if (hash && ['api', 'tutorial', 'router'].includes(hash)) {
			this.setState({ activeTab: hash });
		}
		
		this._onHashChange = () => {
			const newHash = window.location.hash.slice(1);
			if (newHash && ['api', 'tutorial', 'router'].includes(newHash)) {
				this.setState({ activeTab: newHash });
			}
		};
		window.addEventListener('hashchange', this._onHashChange);
		
		this._onKeydown = (e) => {
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault();
				this.setState({ showSearch: true });
			}
			if (e.key === 'Escape') {
				const wasOpen = this.getState().showSearch;
					this.setState({ showSearch: false, searchQuery: '' });
				if (wasOpen) {
					setTimeout(() => {
						const trigger = this._searchTrigger || document.querySelector('.toolbar-search-btn');
						if (trigger) trigger.focus();
					}, 0);
				}
			}
			if (e.key === 'Tab' && this.getState().showSearch) {
				const searchModal = document.querySelector('.search-modal');
				if (!searchModal) return;
				const focusableElements = searchModal.querySelectorAll('input, button, [href], select, textarea, [tabindex]:not([tabindex="-1"])');
				if (focusableElements.length === 0) return;
				const firstElement = focusableElements[0];
				const lastElement = focusableElements[focusableElements.length - 1];
				
				if (e.shiftKey && document.activeElement === firstElement) {
					e.preventDefault();
					lastElement.focus();
				} else if (!e.shiftKey && document.activeElement === lastElement) {
					e.preventDefault();
					firstElement.focus();
				}
			}
			if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && this.getState().showSearch) {
				const searchModal = document.querySelector('.search-modal');
				if (!searchModal) return;
				const resultItems = searchModal.querySelectorAll('.search-result-item[role="option"]');
				if (resultItems.length === 0) return;
				const currentIndex = Array.from(resultItems).indexOf(document.activeElement);
				let nextIndex;
				if (e.key === 'ArrowDown') {
					nextIndex = currentIndex + 1 >= resultItems.length ? 0 : currentIndex + 1;
				} else {
					nextIndex = currentIndex - 1 < 0 ? resultItems.length - 1 : currentIndex - 1;
				}
				e.preventDefault();
				resultItems[nextIndex].focus();
				searchModal.querySelector('.search-results').setAttribute('aria-activedescendant', `search-result-${nextIndex}`);
			}
		};
		document.addEventListener('keydown', this._onKeydown);
	},
	unmounted() {
		if (this._onHashChange) window.removeEventListener('hashchange', this._onHashChange);
		if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
	},
	methods() {
		return {
			setTab(tab) {
				if (this.getState().activeTab === tab) return;
				playBloop(0.8 + (tab === 'api' ? 0.1 : tab === 'tutorial' ? 0.2 : 0.3));
				window.location.hash = tab;
				this.setState({ activeTab: tab, searchQuery: '', selectedCategory: 'all', selectedTag: 'all' });
			},
			setSearch(query) {
				this.setState({ searchQuery: query });
			},
			setCategory(category) {
				playPop();
				this.setState({ selectedCategory: category });
			},
			setTag(tag) {
				playPop();
				this.setState({ selectedTag: tag });
			},
			copyCode(code) {
				showToast('Copied to clipboard', { type: 'success' });
				playChime();
			},
			toggleSearch() {
				const wasOpen = this.getState().showSearch;
				if (wasOpen) {
					this.setState({ showSearch: false, searchQuery: '' });
					setTimeout(() => {
						const trigger = this._searchTrigger || document.querySelector('.toolbar-search-btn');
						if (trigger) trigger.focus();
					}, 0);
				} else {
					this._searchTrigger = document.activeElement;
					this.setState({ showSearch: true });
					setTimeout(() => {
						const input = document.querySelector('.search-modal input');
						if (input) input.focus();
					}, 50);
				}
			}
		};
	},
	render() {
		const { activeTab, searchQuery, selectedCategory, selectedTag, showSearch } = this.getState();

		let filteredData = apiData;
		if (searchQuery) {
			filteredData = filterBySubstring(searchQuery, apiData);
		}
		if (selectedCategory !== 'all') {
			filteredData = filteredData.filter(item => item.category === selectedCategory);
		}
		if (selectedTag !== 'all') {
			filteredData = filteredData.filter(item => item.tags && item.tags.includes(selectedTag));
		}

		const categories = [
			{ id: 'all', label: 'All', icon: '📚' },
			{ id: 'core', label: 'Core', icon: '⚙️' },
			{ id: 'state', label: 'State', icon: '💾' },
			{ id: 'lifecycle', label: 'Lifecycle', icon: '🔄' },
			{ id: 'router', label: 'Router', icon: '🛣️' }
		];

		const tabs = [
			{ id: 'api', label: 'API Explorer', icon: '🔍' },
			{ id: 'tutorial', label: 'Tutorial', icon: '🎮' },
			{ id: 'router', label: 'Router', icon: '🛣️' }
		];

		return (
			<div class="page-learn">
				<div class="learn-hero">
					<div class="learn-hero-content">
						<div class="learn-badge"><span aria-hidden="true">✨</span> Interactive</div>
						<h1>Learn LiteralJS</h1>
						<p class="learn-subtitle">
							Explore the API, follow tutorials, and build things!
						</p>
					</div>
				</div>

				<div class="learn-toolbar">
					<div class="toolbar-tabs">
						{tabs.map(tab => (
							<button
								key={tab.id}
								class={"toolbar-tab" + (activeTab === tab.id ? " -active" : "")}
								events={{ click: () => this.setTab(tab.id) }}
							>
								<span class="tab-icon">{tab.icon}</span>
								<span class="tab-label">{tab.label}</span>
							</button>
						))}
					</div>
					<button
						class="toolbar-search-btn"
						events={{ click: () => this.toggleSearch() }}
						aria-label="Open search (Cmd+K)"
					>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
							<circle cx="11" cy="11" r="8"></circle>
							<path d="m21 21-4.3-4.3"></path>
						</svg>
						<span class="search-btn-text">Search</span>
						<kbd>⌘K</kbd>
					</button>
				</div>

				{showSearch && (
					<div 
						class="search-overlay -active"
						events={{ click: (e) => {
							if (e.target === e.currentTarget) {
								this.setState({ showSearch: false, searchQuery: '' });
								setTimeout(() => {
									const trigger = this._searchTrigger || document.querySelector('.toolbar-search-btn');
									if (trigger) trigger.focus();
								}, 0);
							}
						}}}
					>
						<div class="search-modal" role="dialog" aria-label="Search LiteralJS API">
							<div class="search-modal-header">
								<div class="search-input-wrapper">
									<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
										<circle cx="11" cy="11" r="8"></circle>
										<path d="m21 21-4.3-4.3"></path>
									</svg>
									<input
										type="text"
										placeholder="Search methods, tags, or descriptions..."
										aria-label="Search LiteralJS API"
										value={searchQuery || ''}
										events={{
											input: (e) => this.setState({ searchQuery: e.target.value })
										}}
										autofocus
									/>
									<kbd>ESC</kbd>
								</div>
								{searchQuery && (
									<div class="search-meta">
										<span class="search-meta-count">{filteredData.length} result{filteredData.length !== 1 ? 's' : ''}</span>
										<button 
											class="search-meta-clear"
											events={{ click: () => this.setState({ searchQuery: '' }) }}
										>
											Clear
										</button>
									</div>
								)}
							</div>
							<div class="search-results" role="listbox" aria-label="Search results">
								{filteredData.slice(0, 10).map((method, index) => (
									<div
										key={method.id}
										id={`search-result-${index}`}
										class="search-result-item"
										role="option"
										tabindex="0"
										aria-selected={index === 0}
										events={{
											click: () => {
												this.setState({ showSearch: false, activeTab: 'api' });
												setTimeout(() => {
													document.getElementById(`method-${method.id}`)?.scrollIntoView({ behavior: 'smooth' });
												}, 100);
											},
											keydown: (e) => {
												if (e.key === 'Enter') {
													this.setState({ showSearch: false, activeTab: 'api' });
													setTimeout(() => {
														document.getElementById(`method-${method.id}`)?.scrollIntoView({ behavior: 'smooth' });
													}, 100);
												}
											}
										}}
									>
										<div class="result-icon">{method.icon || '🔧'}</div>
										<div class="result-info">
											<div class="result-title">{method.name}</div>
											<div class="result-meta">
												<span class="result-category">{method.category}</span>
												<span class="result-desc">{method.description}</span>
											</div>
										</div>
										<div class="result-arrow">
											<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
												<polyline points="9 18 15 12 9 6"></polyline>
											</svg>
										</div>
									</div>
								))}
								{filteredData.length === 0 && searchQuery && (
									<div class="search-result-item -empty" role="status">
										<div class="result-icon">🔍</div>
										<div class="result-info">
											<div class="result-title">No results found</div>
											<div class="result-desc">Try a different search term</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{activeTab === 'api' && (
					<div class="api-explorer">
						<div class="api-filters">
							<div class="category-filters">
								{categories.map(cat => (
									<button
										key={cat.id}
										class={"category-btn" + (selectedCategory === cat.id ? " -active" : "")}
										events={{ click: () => this.setCategory(cat.id) }}
									>
										<span>{cat.icon}</span>
										{cat.label}
									</button>
								))}
							</div>
							<div class="tag-filters">
								<span class="tag-filter-label">Filter by tag:</span>
								{['all', 'factory', 'state', 'events', 'router', 'performance'].map(tag => (
									<button
										key={tag}
										class={"tag-btn" + (selectedTag === tag ? " -active" : "")}
										events={{ click: () => this.setTag(tag) }}
									>
										{tag === 'all' ? 'All Tags' : tag}
									</button>
								))}
							</div>
						</div>

						<div class="api-stats" role="status" aria-live="polite">
							<span>Showing {filteredData.length} of {apiData.length} methods</span>
						</div>

						<div class="methods-grid">
							{filteredData.map((method, i) => (
								<MethodCard
									key={method.id}
									method={method}
									onCopy={(code) => this.copyCode(code)}
									index={i}
								/>
							))}
						</div>

						{filteredData.length === 0 && (
							<div class="no-results" role="status" aria-live="polite">
								<div class="no-results-icon">
									<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
										<circle cx="11" cy="11" r="8"></circle>
										<path d="m21 21-4.3-4.3"></path>
									</svg>
								</div>
								<h3>No methods found</h3>
								<p>Try adjusting your search or category filter</p>
							</div>
						)}
					</div>
				)}

				{activeTab === 'tutorial' && (
					<TodoLesson />
				)}

				{activeTab === 'router' && (
					<RouterDocs />
				)}
			</div>
		);
	}
});